import { type FC, useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlavorsSelection } from "@jti/fe-common";
import type { ApiMagentoConsumablesContentsType, ApiMagentoPurchaseType } from "@jti/magento-types";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { MultipleFlavorsSelector } from "@jti/ui/components/MultipleFlavorsSelector";
import { SingleFlavorSelector, type SingleFlavorSelectorItem } from "@jti/ui/components/SingleFlavorSelector";

import { useConsumablesProductDetailsContext } from "@product-details/providers/ConsumablesProductDetailsProvider";
import { getInitialSticks } from "@product-details/utils/getInitialSticks";
import { getMixedConsumableFlavors } from "@product-details/utils/getMixedConsumableFlavors";
import { getSingleFlavorConsumable } from "@product-details/utils/getSingleFlavorConsumable";

import { useConsumablesStockStatuses } from "../../hooks/useConsumablesStockStatuses";
import { filterOutOfStockItems } from "../../utils/filterOutOfStockItems";
import { ConsumablesSectionTitle } from "../ConsumablesFormSectionTitle";

import styles from "./ConsumablesFlavorSelectorSection.module.scss";

export type FlavorSelectorSubmitHandlerPayload = { sku: string } | { flavorsSelection: FlavorsSelection };

export type ConsumablesFlavorSelectorSubmitHandler = (payload: FlavorSelectorSubmitHandlerPayload) => void;

export type ConsumablesFlavorSelectorSectionProps = {
  step: number;
  contentsType: ApiMagentoConsumablesContentsType;
  purchaseType: ApiMagentoPurchaseType;
  shouldValidate?: boolean;
  onFlavorChangeSubmit: ConsumablesFlavorSelectorSubmitHandler;
};

export const ConsumablesFlavorSelectorSection: FC<ConsumablesFlavorSelectorSectionProps> = ({
  step,
  contentsType,
  purchaseType,
  onFlavorChangeSubmit,
  shouldValidate = false,
}) => {
  const { displayOutOfStockStickProducts, displayAvailableInStoresStickProducts } = useConfig();

  const { consumablesProductDetailsComponentData, currentMatrixItem } = useConsumablesProductDetailsContext();
  const stockStatusesQueryState = useConsumablesStockStatuses();

  const { items: consumablesMatrixItems } = consumablesProductDetailsComponentData.consumablesMatrixData;
  const elementsStockStatus = stockStatusesQueryState.data ?? {};

  const singleFlavorItems: SingleFlavorSelectorItem[] = useMemo(() => {
    if (contentsType === "mixed") {
      return [];
    }

    const items = consumablesMatrixItems.filter((item) => {
      return (
        item.packagingFormatId === currentMatrixItem.packagingFormatId &&
        item.purchaseType === purchaseType &&
        item.strengthDetails?.rating === currentMatrixItem.strengthDetails?.rating
      );
    });

    const consumablesItems = items.map(getSingleFlavorConsumable);

    if (displayOutOfStockStickProducts && displayAvailableInStoresStickProducts) {
      return consumablesItems;
    }

    return filterOutOfStockItems(
      consumablesItems,
      displayOutOfStockStickProducts,
      displayAvailableInStoresStickProducts,
      elementsStockStatus
    );
  }, [
    contentsType,
    consumablesMatrixItems,
    displayOutOfStockStickProducts,
    displayAvailableInStoresStickProducts,
    currentMatrixItem.packagingFormatId,
    purchaseType,
    elementsStockStatus,
    currentMatrixItem.strengthDetails?.rating,
  ]);

  const singleFlavorInitialSelection = useMemo(() => {
    if (contentsType === "mixed" || !currentMatrixItem) {
      return null;
    }

    return getSingleFlavorConsumable(currentMatrixItem);
  }, [currentMatrixItem, contentsType]);

  const selectedBundleConfig = currentMatrixItem?.product.productBundleConfiguration?.items?.[0];

  const initialMixedFlavorsSelection = useMemo(() => {
    if (contentsType !== "mixed" || !selectedBundleConfig?.allOptions || !stockStatusesQueryState.isSuccess) {
      return undefined;
    }

    return getInitialSticks(selectedBundleConfig.allOptions, selectedBundleConfig.qty, elementsStockStatus);
  }, [contentsType, elementsStockStatus, selectedBundleConfig, stockStatusesQueryState.isSuccess]);

  const getMixedConsumablesFlavorOptions = () => {
    if (contentsType !== "mixed" || !selectedBundleConfig?.allOptions) {
      return [];
    }

    const mixedFlavorBundleItems = selectedBundleConfig.allOptions.map(getMixedConsumableFlavors);

    if (displayOutOfStockStickProducts && displayAvailableInStoresStickProducts) {
      return mixedFlavorBundleItems;
    }

    return filterOutOfStockItems(
      mixedFlavorBundleItems,
      displayOutOfStockStickProducts,
      displayAvailableInStoresStickProducts,
      elementsStockStatus
    );
  };

  const onSelectedFlavourChange = (sku: string) => {
    onFlavorChangeSubmit({ sku });
  };

  const setSelectedFlavors = (flavorsSelection: FlavorsSelection) => {
    onFlavorChangeSubmit({ flavorsSelection });
  };

  return (
    <ConsumablesFormStepSection
      title={
        <ConsumablesSectionTitle
          translationKey="consumablesForm.section.flavor_selector_section.title"
          stepCount={step}
        />
      }
      data-testid="consumables-flavor-selector"
    >
      {contentsType === "mixed" && stockStatusesQueryState.isSuccess && (
        <MultipleFlavorsSelector
          key={currentMatrixItem.sku}
          limit={selectedBundleConfig?.qty ?? 0}
          items={getMixedConsumablesFlavorOptions()}
          stockStatusElements={elementsStockStatus}
          onChange={setSelectedFlavors}
          defaultFlavorsSelection={initialMixedFlavorsSelection}
          isProductDataLoading={stockStatusesQueryState.isLoading}
          shouldValidate={shouldValidate}
          className={styles.flavorSelector}
        />
      )}

      {contentsType !== "mixed" && singleFlavorInitialSelection && (
        <SingleFlavorSelector
          key={currentMatrixItem.sku}
          flavors={singleFlavorItems}
          initialSelection={singleFlavorInitialSelection}
          stockStatusElements={elementsStockStatus}
          isProductDataLoading={stockStatusesQueryState.isLoading}
          onChange={onSelectedFlavourChange}
          className={styles.flavorSelector}
          strengthDetails={currentMatrixItem.strengthDetails ?? undefined}
        />
      )}
    </ConsumablesFormStepSection>
  );
};
